import type { KeyboardEvent, ChangeEvent, RefObject } from 'react';
import React, { Component, createRef, useContext } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, injectIntl } from 'react-intl-next';
import debounce from 'lodash/debounce';
import memoize from 'lodash/memoize';

import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { eventHub } from '@atlassian/editor-plugin-ai/EventHub';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AIBorder } from '@atlassian/ai-components';

import { isMac } from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import { ALLOW_SHORTCUTS_CLASS } from '@confluence/shortcuts/entry-points/constants';
import { KeyboardEventKeys } from '@confluence/keyboard-events';
import type {
	FlagDescriptor,
	FlagsStateContainer,
} from '@confluence/flags/entry-points/FlagsStateContainer';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { buildEditorFlagMessage } from '@confluence/editor-flags/entry-points/buildEditorFlagMessage';
import { ALLOWED_FLAG_MESSAGES } from '@confluence/editor-flags/entry-points/allowedFlagConstants';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker';
import { AI_TITLE_GENERATION } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset';
import { withIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import {
	RedactionsTourSteps,
	RedactionTourStepSource,
	REDACTIONS_TOUR_TARGETS,
	useRedactionsTour,
	useTrackTitleRedactionRendered,
} from '@confluence/redactions/entry-points/tour';
import { onChangeTitleDebounceTime } from '@confluence/content-types-utils/entry-points/contentTypeConstants';
import { fg } from '@confluence/feature-gating';

import {
	EditorTitleTextArea,
	AIGeneratedEditorTitleTextArea,
	EditorTitleTextAreaNext,
	// eslint-disable-next-line import/no-cycle
} from './editorTitleStyled';
import { EditorTitleContainer } from './shared-components/EditorTitleContainer';
import { isFieldSizingSupported } from './utils/browserSupport';
import { EditorTitleAIGenerationOnPaste } from './EditorTitleAIGenerationOnPaste';

// The duration is hard-coded in styled bc compiled css will not allow us to insert this timing dynamically. It's also slightly longer to account for easing out
const ERROR_ANIMATION_DURATION = 1200;
export const MAX_LENGTH_OF_TITLE = 255;
export const OFFLINE_ERROR_MESSAGE = 'Cannot send metadata, currently offline.';

const i18n = defineMessages({
	blogpostTitlePlaceholder: {
		id: 'editor-title.existing.blogpost.placeholder',
		defaultMessage: 'Blog post title',
		description: 'Placeholder for Blog post title text area',
	},
	editorOnboardingExpTitlePlaceholderText: {
		id: 'editor-title.editor.onboarding.title.placeholder',
		defaultMessage: 'Give this page a title',
		description: 'Placeholder for editor onboarding title textarea',
	},
	aiGeneratedTitleDismissButton: {
		id: 'editor-title.ai.title.error.dismiss',
		defaultMessage: 'Dismiss',
		description:
			'The button dismisses the flag shown when an error occurred trying to generate a title with AI',
	},
	createBlogpostTitleDescription: {
		id: 'editor-title.description.add.blogpost',
		defaultMessage: ', Add Blog Post',
		description:
			"An description of the add blog post page's purpose for accessibility. Should mirror title.add.blogpost.  The comma at the beginning of this message causes screen readers to pause before reading this message.  Please include a comma or similar punctuation when translating this.",
	},
	editPageTitleDescription: {
		id: 'editor-title.description.edit.page',
		defaultMessage: ', Edit',
		description:
			"An description of the edit page's purpose for accessibility. Should mirror document-title.edit.name.  The comma at the beginning of this message causes screen readers to pause before reading this message.  Please include a comma or similar punctuation when translating this.",
	},
	createPageTitleDescription: {
		id: 'editor-title.description.add.page',
		defaultMessage: ', Add Page',
		description:
			"An description of the add page's purpose for accessibility. Should mirror title.add.page.  The comma at the beginning of this message causes screen readers to pause before reading this message.  Please include a comma or similar punctuation when translating this.",
	},
	createPageTemplateTitleDescription: {
		id: 'editor-title.description.add.page.template',
		defaultMessage: ', Create template',
		description:
			"An description of the add page template page's purpose for accessibility. Should mirror title.add.page.template.  The comma at the beginning of this message causes screen readers to pause before reading this message.  Please include a comma or similar punctuation when translating this.",
	},
	editPageTemplateTitleDescription: {
		id: 'editor-title.description.edit.page.template',
		defaultMessage: ', Edit template',
		description:
			"An description of the edit page template page's purpose for accessibility. Should mirror title.edit.page.template.  The comma at the beginning of this message causes screen readers to pause before reading this message.  Please include a comma or similar punctuation when translating this.",
	},
});

/*
  Adding this is necessary as the AI suggest title feature works by checking for the presence of this custom data attribute
*/
export const AI_SUGGEST_TITLE_PROPS = {
	'data-ai-suggest-title-target': 'true',
};

export type EditorTitleProps = {
	contentId: string;
	cloudId: string;
	title: string | null;
	customPlaceholder?: string;
	initialShouldFocusTitle?: boolean;
	onChange: (value: string) => void;
	onFocusChanged: (focusChanged: boolean) => void;
	onTitleBlur: () => void;
	focusEditor: () => void;
	setGeneratedTitle: (value: string) => void | undefined;
	setTrackAIResultViewStored: (attribute: any) => void | undefined;
	trackAIInteractionInit: () => void;
	trackAIResultView: (attribute: any) => void;
	trackAIResultError: (attribute: any) => void;
	activeRedactionsSpotlight: React.JSX.Element | null;
	experienceTracker: ExperienceTrackerAPI;
	isAIEnabledForContent: boolean;
	isDisabled?: boolean;
	debounceOnChange?: boolean;
	contentType: 'page' | 'blogpost' | null;
	userOffline?: boolean;
	isInPreview?: boolean;
	isTemplateEditor?: boolean;
	templateSuggestionsRef?: RefObject<HTMLDivElement> | null;
	templateButtonRef?: RefObject<HTMLElement> | null;
	isTemplatesSuggestionsEnabled?: boolean;
	flagArray?: Array<FlagDescriptor>;
	flags: FlagsStateContainer;
	isTitleAIGenerated?: boolean;
	isTitleCenterAligned?: boolean;
	isUnpublishedDraft?: boolean;
	isEditorEmpty?: boolean;
	isEditMode?: boolean;
	spaceKey: string;
	spaceId?: string;
	editorApi?: FullPageEditorPresetAPI;
	editorSessionId?: string | null;
	isTitleGeneratingFromPaste?: boolean;
	onTitleGeneratingChange?: (isGenerating: boolean) => void;
} & WrappedComponentProps &
	WithAnalyticsEventsProps;

type EditorTitleState = {
	title: string | null;
	prevTitle: string | null;
	shouldFocusTitle: boolean;
	focusChanged: boolean;
	shouldAnimateTitle: boolean;
	isTitleAIGenerated: boolean;
	isTitleGenerating: boolean;
};

const AIBorderStyles = xcss({
	backgroundColor: 'elevation.surface',
});

const PlaceholderStyles = xcss({
	// Necessary for proper editor content/title alignment
	marginTop: 'space.negative.075',
	marginBottom: 'space.050',
});

const TitleGenerationPlaceholder = () => {
	return (
		<>
			<Box padding="space.200" />
			<Box padding="space.025" />
		</>
	);
};
TitleGenerationPlaceholder.displayName = 'TitleGenerationPlaceholder';

export const fieldSizingEnabled = () => {
	// we manually expose here: next/packages/editor-title/src/editorTitleStyled.tsx line ~160
	if (!expValEquals('platform_editor_editor_title_field_sizing', 'variant', 'test')) {
		return false;
	}

	return isFieldSizingSupported();
};

/**
 * @todo CTE-268 typing in title is slow with in-editor templates (long term solution)
 * For now, we are solving this by debouncing calls to the onChange prop. Once the long term
 * solution is ready (see ticket for details), we should remove the debounceOnChange prop and logic
 */
class EditorTitleComponentClass extends Component<EditorTitleProps, EditorTitleState> {
	_textareaRef: RefObject<HTMLTextAreaElement>;
	_hasEditEventFiredRef: React.MutableRefObject<boolean>;

	static getDerivedStateFromProps(props: EditorTitleProps, state: EditorTitleState) {
		if (props.title !== state.prevTitle) {
			return {
				title: props.title,
				prevTitle: props.title,
			};
		}

		return null;
	}

	constructor(props: EditorTitleProps) {
		super(props);

		this._textareaRef = createRef<HTMLTextAreaElement>();
		this._hasEditEventFiredRef = { current: false };
	}

	state = {
		title: this.props.title,
		// eslint-disable-next-line react/no-unused-state
		prevTitle: this.props.title,
		shouldFocusTitle: this.props.initialShouldFocusTitle ?? true,
		focusChanged: false,
		shouldAnimateTitle: false,
		isTitleAIGenerated: false,
		isTitleGenerating: false,
	};

	componentDidMount() {
		const { isTemplateEditor, title } = this.props;
		const { shouldFocusTitle } = this.state;
		const shouldTemplateEditorTitleFocus = isTemplateEditor && !title && shouldFocusTitle;

		if (shouldTemplateEditorTitleFocus) {
			this._focusTextarea();
		}

		this._updateHeightAuto();
		window.addEventListener('resize', this._onWindowResize);
	}

	componentDidUpdate(prevProps: EditorTitleProps, prevState: EditorTitleState) {
		const {
			userOffline,
			focusEditor,
			title,
			isInPreview,
			flagArray,
			intl,
			isTitleGeneratingFromPaste,
			flags,
		} = this.props;
		const { focusChanged, shouldFocusTitle } = this.state;
		const userWasOffline = prevProps.userOffline && !userOffline;
		const userWasNotOffline = !prevProps.userOffline && !userOffline;

		this._updateHeightAuto();
		if (prevState.prevTitle !== title) {
			this._debouncedOnChange.cancel();
		}
		// if user was offline
		if (userWasOffline) {
			if (focusChanged && shouldFocusTitle) {
				this._focusTextarea();
			} else {
				focusEditor();
			}
		}
		// if user was not offline
		if (userWasNotOffline && !title && shouldFocusTitle) {
			this._focusTextarea();
		}

		/** Focus title if title-related error flag:
		 * We check the flags array length against that of the flags from prevProps so that
		 * we can detect if a new error flag has been added. If the current length is greater
		 * than the previous length, a flag was added.
		 */
		if (
			flagArray &&
			prevProps.flagArray &&
			flagArray.length > prevProps.flagArray.length &&
			this._hasTitleError()
		) {
			this._onTitleError();
		}

		if (prevProps.isTitleGeneratingFromPaste && !isTitleGeneratingFromPaste) {
			const flagMessage = buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE_FROM_PASTE]: true },
				formatMessage: intl.formatMessage,
			});

			void flags.hideFlag('ai-generated-title-from-paste-flag');
			void flags.showInfoFlag({
				id: 'ai-generated-title-from-paste-flag',
				title: flagMessage?.title,
				description: flagMessage?.description as React.ReactNode,
				actions: [
					{
						content: intl.formatMessage(i18n.aiGeneratedTitleDismissButton),
						onClick: () => flags.hideFlag('ai-generated-title-from-paste-flag'),
					},
				],
			});

			this._focusTextarea();
		}

		if (prevProps.title === title && prevProps.isInPreview === isInPreview) {
			return;
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._onWindowResize); // cleanup event listener
		this._debouncedOnChange.flush();
	}

	/**
	 * Checks if the flags array has a title-relevant error
	 * 1. empty title / title with only whitespace
	 * 2. duplicate title
	 */
	_hasTitleError = memoize(() => {
		const { flagArray, intl } = this.props;

		// These flag messages are always defined so they shouldn't return null
		const titleErrors = [
			buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.INVALID_PAGE_TITLE]: true },
				formatMessage: intl.formatMessage,
			})?.title,
			buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE]: true },
				formatMessage: intl.formatMessage,
			})?.title,
			buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.TITLE_REQUIRED]: true },
				formatMessage: intl.formatMessage,
			})?.title,
			buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE]: true },
				formatMessage: intl.formatMessage,
			})?.title,
			buildEditorFlagMessage({
				errorData: {
					[ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE_WITHIN_EDITOR]: true,
				},
				formatMessage: intl.formatMessage,
			})?.title,
		];

		if (flagArray && flagArray.length > 0) {
			// We only want to check the most recent, incoming error in case user hasn't dismissed a previous error
			const errorMessage = String(flagArray[0].title);
			return titleErrors.includes(errorMessage);
		}
		return false;
	});

	_focusTextarea() {
		if (this._textareaRef.current) {
			this._textareaRef.current.focus();
		}
	}

	_debouncedOnChange = debounce((value) => {
		this.props.onChange(value);
	}, onChangeTitleDebounceTime);

	_onTitleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		const { createAnalyticsEvent, contentId, spaceId } = this.props;
		const newTitle = event.target.value;
		this.setState({ title: newTitle, isTitleAIGenerated: false });

		if (this.props.debounceOnChange) {
			this._debouncedOnChange(newTitle);
		} else {
			this.props.onChange(newTitle);
		}

		if (!this._hasEditEventFiredRef.current) {
			createAnalyticsEvent?.({
				type: 'sendTrackEvent',
				data: {
					source: 'editorTitle',
					action: 'edited',
					actionSubject: 'editor',
					objectId: contentId,
					objectType: 'page',
					attributes: { isLivePage: false, editorSessionId: this.props.editorSessionId },
					containerType: 'space',
					containerId: spaceId,
				},
			}).fire();
			this._hasEditEventFiredRef.current = true;
		}
	};

	_onFocusChanged = (focusChanged: boolean) => {
		this.setState({
			shouldFocusTitle: focusChanged,
			focusChanged: true,
		});
		this.props.onFocusChanged(focusChanged);
	};

	_setTitleAI = (newTitle: string) => {
		this.setState({ isTitleGenerating: false, title: newTitle, isTitleAIGenerated: true });
		this.props.onChange(newTitle);
	};

	generateTitle = () => {
		const {
			trackAIResultError,
			trackAIResultView,
			trackAIInteractionInit,
			setGeneratedTitle,
			setTrackAIResultViewStored,
			experienceTracker,
			userOffline,
			flags,
			intl,
		} = this.props;

		if (userOffline && fg('cc-page-experiences-disable-title-changes-offline')) {
			return;
		}

		const filteredProseMirrors = Array.from(document.querySelectorAll('.ProseMirror')).filter(
			(el) => !el.closest('[data-testid="chromeless-editor"]'),
		);
		const targetEditorId = (filteredProseMirrors[0] as HTMLElement)?.dataset?.editorId;

		const handleAiTitleError = ({
			errorMessage,
			errorCode = 500,
		}: {
			errorMessage?: string;
			errorCode?: number;
		}) => {
			this._setTitleAI('');
			trackAIResultError({
				aiErrorMessage: errorMessage,
				aiErrorCode: errorCode,
			});

			if (errorMessage === OFFLINE_ERROR_MESSAGE) {
				// Abort rather than fail because the editor being offline
				// should not be attributed to AI Title Generation experience failure
				experienceTracker.abort({
					name: AI_TITLE_GENERATION,
					reason: 'Editor offline',
				});
			} else {
				experienceTracker.fail({
					name: AI_TITLE_GENERATION,
					error: new Error(errorMessage),
				});
			}
			this.setState({ isTitleGenerating: false });

			const errorFlagMessage = buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE_ERROR]: true },
				formatMessage: intl.formatMessage,
			});

			void flags.hideFlag('ai-generated-title-flag');
			void flags.showErrorFlag({
				id: 'ai-generated-title-error-flag',
				title: errorFlagMessage?.title,
				description: errorFlagMessage?.description as React.ReactNode,
				actions: [
					{
						content: intl.formatMessage(i18n.aiGeneratedTitleDismissButton),
						onClick: () => flags.hideFlag('ai-generated-title-error-flag'),
					},
				],
			});
		};

		let generatedTitle = '';

		if (targetEditorId) {
			trackAIInteractionInit();
			setTrackAIResultViewStored?.(trackAIResultView);
			experienceTracker.start({
				name: AI_TITLE_GENERATION,
			});
			eventHub.publishToEditor({
				event: 'start prompt',
				data: {
					targetEditorId,
					analyticSourceId: 'confluenceTitleGenOnPublish',
					prompt: 'suggest title',
					hideModal: true,
					streamingCallback: (data) => {
						switch (data.type) {
							case 'initialising':
								this.setState({ isTitleGenerating: true });
								break;
							case 'streaming':
								generatedTitle = data.result?.trim() ?? '';
								if (generatedTitle) {
									this._setTitleAI(generatedTitle);
								}
								break;
							case 'complete':
								generatedTitle = data.result?.trim() ?? '';
								if (generatedTitle) {
									this._setTitleAI(generatedTitle);
									trackAIResultView({ doesNotMeetMAUCriteria: true });
									experienceTracker.succeed({
										name: AI_TITLE_GENERATION,
									});
									setGeneratedTitle?.(generatedTitle);
								} else {
									handleAiTitleError({
										errorMessage: 'Generated title is empty',
									});
								}
								break;
							case 'error':
								handleAiTitleError({
									errorMessage: data.errorInfo?.errorContent,
									errorCode: data.errorInfo?.statusCode,
								});
								break;
						}
					},
				},
			});
		}
	};

	generateDescription = () => {
		const {
			intl: { formatMessage },
			contentType,
			isTemplateEditor,
			isUnpublishedDraft,
		} = this.props;

		if (isUnpublishedDraft) {
			if (isTemplateEditor) {
				return formatMessage(i18n.createPageTemplateTitleDescription);
			} else {
				return contentType === 'blogpost'
					? formatMessage(i18n.createBlogpostTitleDescription)
					: formatMessage(i18n.createPageTitleDescription);
			}
		} else {
			return isTemplateEditor
				? formatMessage(i18n.editPageTemplateTitleDescription)
				: formatMessage(i18n.editPageTitleDescription);
		}
	};

	_onTitleError = () => {
		const { createAnalyticsEvent, flagArray, intl, contentId, spaceId, isAIEnabledForContent } =
			this.props;

		const flagTitle = String(flagArray?.[0]?.title);

		const titleRequiredFlagTitle = buildEditorFlagMessage({
			errorData: { [ALLOWED_FLAG_MESSAGES.TITLE_REQUIRED]: true },
			formatMessage: intl.formatMessage,
		})?.title;

		const aiGeneratedTitleFlagTitle = buildEditorFlagMessage({
			errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE]: true },
			formatMessage: intl.formatMessage,
		})?.title;

		if (
			flagArray &&
			(flagTitle === titleRequiredFlagTitle || flagTitle === aiGeneratedTitleFlagTitle)
		) {
			createAnalyticsEvent?.({
				type: 'sendTrackEvent',
				data: {
					source: 'editorTitle',
					action: 'publishAttemptedEmptyTitle',
					actionSubject: 'editor',
					objectId: contentId,
					objectType: 'page',
					attributes: {
						isLivePage: false,
						isAiTitleEnabled:
							String(flagArray?.[0]?.title) ===
							buildEditorFlagMessage({
								errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE]: true },
								formatMessage: intl.formatMessage,
							})?.title,
					},
					containerType: 'space',
					containerId: spaceId,
				},
			}).fire();
		}

		if (
			flagArray &&
			String(flagArray?.[0]?.title) ===
				buildEditorFlagMessage({
					errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE]: true },
					formatMessage: intl.formatMessage,
				})?.title &&
			isAIEnabledForContent &&
			expValEquals('confluence_ai_title_generation', 'cohort', 'test')
		) {
			this._focusTextarea();
			this.generateTitle();
		} else {
			this._focusTextarea();

			this.setState({ shouldAnimateTitle: true });
			/*
			 * Additional focus needed after animation bc setting the shouldAnimateTitle state will change the key of the textArea,
			 * which means re-render will regenerate entire view, causing loss of focus
			 */
			setTimeout(() => {
				this.setState({ shouldAnimateTitle: false });
				this._focusTextarea();
			}, ERROR_ANIMATION_DURATION);
		}
	};

	_getTitlePlaceholder = () => {
		const { contentType, intl, customPlaceholder } = this.props;
		if (customPlaceholder) {
			return customPlaceholder;
		}
		return contentType === 'blogpost'
			? intl.formatMessage(i18n.blogpostTitlePlaceholder)
			: intl.formatMessage(i18n.editorOnboardingExpTitlePlaceholderText);
	};

	_updateHeightAuto() {
		// if the browser doesn't support `field-sizing: content`,
		// this needs to run to set the height of the textarea
		if (!fieldSizingEnabled()) {
			if (this._textareaRef.current) {
				// this line will make a reflow so that `textArea.scrollHeight` is updated with correct value.
				this._textareaRef.current.style.height = 'auto';
				// In SSR environment, the previous line does not cause a reflow, so the `scrollHeight` stays zero
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (!process.env.REACT_SSR) {
					this._textareaRef.current.style.height = `${this._textareaRef.current.scrollHeight}px`;
				}
			}
		}
	}

	_onFocus = () => {
		this._onFocusChanged(true);
	};

	_onBlur = () => {
		this.props.onTitleBlur();
		this._onFocusChanged(false);
	};

	/**
	 * Triggered from window resize event listener
	 */
	_onWindowResize = debounce(() => {
		this._updateHeightAuto();
	}, 200);

	_onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		// If the user is composing characters using, e.g., the Hiragana - Kana keyboard,
		// short circuit the code to prevent the bug in Chrome where the selected text
		// gets copied into the editor body.
		if (event.nativeEvent.isComposing) {
			return;
		}

		// Shift+Tab should not be prevented, so we check if tab pressed without shift
		const isTabPressed = event.key === KeyboardEventKeys.TAB.key && !event.shiftKey;
		// If enter is pressed without publish keyboard shortcut prompt key, prevent default and insert newline in editor body
		const promptKeyForPublishShortcut = isMac() ? event.metaKey : event.ctrlKey;
		const isEnterPressed =
			event.key === KeyboardEventKeys.ENTER.key && !promptKeyForPublishShortcut;
		const isDownArrowPressed = event.key === KeyboardEventKeys.DOWN.key;

		if (isTabPressed || isEnterPressed || isDownArrowPressed) {
			event.preventDefault();
			event.stopPropagation();
			if (this.props.isTemplatesSuggestionsEnabled && this.props.templateButtonRef?.current) {
				this.props.templateSuggestionsRef?.current?.focus();
			} else {
				const { editorApi } = this.props;
				if (isEnterPressed) {
					editorApi?.core?.actions.execute(
						editorApi?.pageTitleUtils?.commands?.insertNewLineAtDocStart(),
					);
				}
				if (isTabPressed || isDownArrowPressed) {
					editorApi?.core?.actions.execute(editorApi?.pageTitleUtils?.commands?.selectDocStart());
				}
				this.props.focusEditor();
			}
		}
	};

	render() {
		const { isDisabled, isTitleCenterAligned, isAIEnabledForContent } = this.props;

		// We store local changes in state when we debounce calls to this.props.onChange.
		// So state contains the most updated title value in this case
		const title = this.props.debounceOnChange ? this.state.title : this.props.title;

		const description = this.generateDescription();

		const placeholder = this._getTitlePlaceholder();

		const UseEditorTextAreaNext = fieldSizingEnabled()
			? EditorTitleTextAreaNext
			: EditorTitleTextArea;

		const EditorTitleTextAreaToUse =
			isAIEnabledForContent &&
			UNSAFE_expValEqualsNoExposure('confluence_ai_title_generation', 'cohort', 'test')
				? AIGeneratedEditorTitleTextArea
				: UseEditorTextAreaNext;

		if (this.state.isTitleGenerating) {
			return (
				<Box xcss={PlaceholderStyles}>
					<AIBorder loading useModalStyles hideGlow customInnerContainerStyles={AIBorderStyles}>
						<TitleGenerationPlaceholder />
					</AIBorder>
				</Box>
			);
		}

		return (
			<SpotlightManager>
				<EditorTitleAIGenerationOnPaste
					contentId={this.props.contentId}
					cloudId={this.props.cloudId}
					title={this.props.title}
					userOffline={this.props.userOffline}
					onTitleGenerated={this._setTitleAI}
					onTitleGeneratingChange={this.props.onTitleGeneratingChange}
					isEditorEmpty={this.props.isEditorEmpty ?? false}
				/>
				<SpotlightTarget name={REDACTIONS_TOUR_TARGETS.TITLE_REDACTION}>
					<EditorTitleContainer>
						<EditorTitleTextAreaToUse
							name="editpages-title"
							data-test-id="editor-title"
							{...AI_SUGGEST_TITLE_PROPS}
							maxLength={MAX_LENGTH_OF_TITLE}
							aria-label={placeholder}
							aria-describedby={
								fg('cc_announce_editor_description_after_title')
									? 'editor-title-description'
									: undefined
							}
							placeholder={placeholder}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className={ALLOW_SHORTCUTS_CLASS}
							value={title || ''}
							required
							rows={1}
							ref={this._textareaRef}
							onKeyDown={this._onKeyDown}
							onChange={this._onTitleChange}
							onFocus={this._onFocus}
							onBlur={this._onBlur}
							data-gramm={false}
							disabled={isDisabled}
							shouldAnimate={this.state.shouldAnimateTitle}
							isTitleAIGenerated={this.state.isTitleAIGenerated}
							isTitleCenterAligned={isTitleCenterAligned}
							key={this.state.shouldAnimateTitle ? 'editor-title-animated' : 'editor-title'} // This key swapping is necessary to trigger and reset the title animation
						/>
						{fg('cc_announce_editor_description_after_title') && (
							<div hidden>
								<VisuallyHidden id="editor-title-description" testId="editor-title-description">
									{description}
								</VisuallyHidden>
							</div>
						)}
					</EditorTitleContainer>
				</SpotlightTarget>
				<SpotlightTransition>{this.props.activeRedactionsSpotlight ?? null}</SpotlightTransition>
			</SpotlightManager>
		);
	}
}

function aiAnalyticsEvents(WrappedComponent) {
	const WithAIAnalyticsFn = function WithAIAnalytics(props) {
		const { trackAIInteractionInit, trackAIResultError, trackAIResultView } =
			useAIEventsInstrumentation();

		return (
			<WrappedComponent
				{...props}
				trackAIInteractionInit={trackAIInteractionInit}
				trackAIResultError={trackAIResultError}
				trackAIResultView={trackAIResultView}
			/>
		);
	};
	WithAIAnalyticsFn.displayName = 'WithAIAnalytics';
	return WithAIAnalyticsFn;
}

const redactionsChangeboarding = (WrappedComponent) => (props) => {
	const { contentId, title } = props;
	useTrackTitleRedactionRendered({ contentId, title });
	const { activeSpotlight } = useRedactionsTour({
		contentId,
		step: RedactionsTourSteps.FIRST,
		firstStepSource: RedactionTourStepSource.titleRedaction,
	});
	return <WrappedComponent {...props} activeRedactionsSpotlight={activeSpotlight} />;
};
redactionsChangeboarding.displayName = 'RedactionsChangeboarding';

const experienceTracking = (WrappedComponent) => (props) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	return <WrappedComponent {...props} experienceTracker={experienceTracker} />;
};
experienceTracking.displayName = 'ExperienceTracking';

export const EditorTitle =
	//Adding HOC since flagArray is needed to trigger componentDidUpdate
	withFlags(
		withIsAIEnabledForContent(
			injectIntl(
				experienceTracking(
					redactionsChangeboarding(
						aiAnalyticsEvents(withAnalyticsEvents()(EditorTitleComponentClass)),
					),
				),
			),
		),
	);
EditorTitle.displayName = 'EditorTitle';
